import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';

import NoFlats from 'components/Buildings/NoBuildings';

import styles from './List.scss';

import AngleDown from 'react-icons/lib/fa/angle-down';
import AngleUp from 'react-icons/lib/fa/angle-up';
import iconLock from 'assets/images/icons/icon-lock.svg';

import nothingFound from 'assets/images/nothingfound.png';
import { withTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import { isReserved } from '../../../../helper';
import { printNumber } from '../../../../utils';
import { gaEvent, trackGA } from 'services/ga';

/*
  Мы должны уметь сортировать в следующем порядке следующие значения:
    A B -004 -1 0 0 00 1 1.1 1.2 1.11 2 +3 +004 004 04 11
  Подвох:
    1 2 11, не 1 11 2, и при этом 1.1 1.2 1.11, не 1.1 1.11 1.2
  В таком же ключе должны уметь сортировать строки содержащие точку, цифры и
  буквы в перемешку:
    A12.A A12.11 В1.M6 В1.M7 233.1
*/
const split = /([^0-9\-\+]+|[\-\+]?[0-9]+|[\-\+])/g;
const digit = /[\-\+]?[0-9]+/;
const stringCompare = (a, b) => {
  const partsA = a.match(split) || [];
  const partsB = b.match(split) || [];
  for (let i = 0; i < Math.max(partsA.length, partsB.length); ++i) {
    const partA = partsA[i] || '';
    const partB = partsB[i] || '';
    if (partA === partB) {
      continue;
    }
    const typeA = digit.test(partA) ? 1 : 0;
    const typeB = digit.test(partB) ? 1 : 0;
    if (typeA !== typeB) {
      return typeA - typeB;
    } else if (typeA === 1) {
      return Number(partA) - Number(partB) || (partA > partB ? 1 : -1);
    }
    const lowerA = partA.toLowerCase();
    const lowerB = partB.toLowerCase();
    return lowerA === lowerB ? (partA > partB ? 1 : -1) : lowerA > lowerB ? 1 : -1;
  }
  return 0;
};

const stringCompareWithNullsFirst = (a, b) => {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return -1;
  }
  if (b === null) {
    return 1;
  }
  return stringCompare(a, b);
};

const numberCompare = (a, b) => Number(a) - Number(b);

const valuesCompare = (list, a, b) => list.indexOf(a) - list.indexOf(b);

const getCols = (t) => ({
  number: {
    name: t('components.building.list.number'),
    nameSm: t('components.building.list.numberS'),
    sort: (a, b) => stringCompareWithNullsFirst(a.data.number, b.data.number),
    headerClassName: styles.HeaderNumber,
  },
  title: {
    name: t('components.building.list.name'),
    nameSm: t('components.building.list.nameS'),
    sort: (a, b) => stringCompare(a.data.number, b.data.number),
    headerClassName: styles.HeaderNumber,
  },
  area: {
    name: t('components.building.list.area'),
    nameSm: t('components.building.list.areaS'),
    sort: (a, b) => numberCompare(a.data.area, b.data.area),
    headerClassName: styles.HeaderArea,
  },
  cost: {
    name: t('components.building.list.cost'),
    nameSm: t('components.building.list.costS'),
    sort: (a, b) => numberCompare(a.data.priceAll, b.data.priceAll),
    headerClassName: styles.HeaderCost,
  },
  price: {
    name: t('components.building.list.price'),
    nameSm: t('components.building.list.priceS'),
    sort: (a, b) => numberCompare(a.data.priceM, b.data.priceM),
    headerClassName: styles.HeaderCostMeter,
  },
  interior: {
    name: t('components.building.list.interior'),
    nameSm: t('components.building.list.interiorS'),
    sort: (a, b) =>
      valuesCompare(
        [
          'zeroInt',
          'chernInt',
          'predChistInt',
          'chistInt',
          'remInt',
          'chastRemInt',
          'whiteBox',
          'repair',
        ],
        a.data.interior,
        b.data.interior
      ),
    headerClassName: styles.HeaderInterior,
  },
  apartments: {
    name: t('components.building.list.apartments'),
    nameSm: t('components.building.list.apartmentsS'),
    sort: (a, b) =>
      valuesCompare(
        ['studioAp', 'oneAp', 'twoAp', 'threeAp', 'allAp', 'penthaus'],
        a.data.cnt_apart,
        b.data.cnt_apart
      ),
    headerClassName: styles.HeaderRooms,
  },
  floor: {
    name: t('components.building.list.floor'),
    nameSm: t('components.building.list.floorS'),
    sort: (a, b) => stringCompare(a.data.floorName, b.data.floorName),
    headerClassName: styles.HeaderFloorNumber,
  },
  floorKp: {
    name: t('components.building.list.floorKp'),
    nameSm: t('components.building.list.floorKpS'),
    sort: (a, b) => stringCompare(a.data.floorName, b.data.floorName),
    headerClassName: styles.HeaderFloorNumber,
  },
  status: {
    name: '',
    nameSm: '',
    sort: false,
    headerClassName: styles.HeaderStatus,
  },
});

class List extends Component {
  constructor(props) {
    super(props);

    const { data, sortColumn } = props;
    const cells = this.getCells(data, sortColumn);
    const otherCells = this.getCells(data, sortColumn, true);

    this.state = {
      cells,
      otherCells,
      selected: 'defaultSelected' in props ? props.defaultSelected : null,
    };

    this.handleCellClick = this.handleCellClick.bind(this);
    this.handleHeaderCellClick = this.handleHeaderCellClick.bind(this);
  }

  componentDidMount() {
    const { defaultScroll, isChessLoading, pavilions, location } = this.props;
    const list = ReactDOM.findDOMNode(this);
    list.scrollTop = defaultScroll[0];
    list.scrollLeft = defaultScroll[1];

    const { cells = [], otherCells = [] } = this.state;
    const cellWithoutReserved = cells;
    const otherCellsWithoutReserved = otherCells;

    if (
      !isChessLoading &&
      cellWithoutReserved.length === 0 &&
      otherCellsWithoutReserved.length === 0
    ) {
      const searchParams = new URLSearchParams(location.search);
      const sectionValue = searchParams.get('section');
      const pavilionIndex = sectionValue ? sectionValue.split('.')[0] : 0;
      const pavilionName = pavilions[pavilionIndex].name;

      this.handleTrackEvent({
        eventName: 'buildingChessListSoldOutCardShow',
        pavilionName,
      });
    }
  }

  componentWillUnmount() {
    const { handleScroll } = this.props;
    const list = ReactDOM.findDOMNode(this);
    const { scrollTop, scrollLeft } = list;
    handleScroll(scrollTop, scrollLeft);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { sortColumn, data } = nextProps;
    const { sortColumn: prevSortColumn, data: prevData } = this.props;

    if (sortColumn !== prevSortColumn || data !== prevData) {
      const { data } = nextProps;
      const cells = this.getCells(data, sortColumn);
      const otherCells = this.getCells(data, sortColumn, true);
      this.setState({ cells, otherCells });
    }
  }

  getCells(data, sortColumn, other) {
    const cells = [];
    const { t } = this.props;
    data.forEach((row) => {
      row.cells
        .filter(
          (cell) => cell && !cell.hidden && (other ? cell.mark === false : cell.mark !== false)
        )
        .forEach((cell) => {
          cells.push(cell);
        });
    });
    const [sortField, sortOrder] = sortColumn;
    cells.sort((a, b) => getCols(t)[sortField].sort(a, b) * (sortOrder ? 1 : -1));
    return cells;
  }

  handleCellClick(row, column, cell) {
    const { handleCellClick } = this.props;
    this.handleTrackEvent({
      eventName: 'buildingChessLotClick',
      cell,
      eventContext: 'spisok',
    });
    handleCellClick([row, column]);
  }

  handleHeaderCellClick(field) {
    let {
      sortColumn: [sortField, sortOrder],
    } = this.props;
    if (sortField === field) {
      sortOrder = !sortOrder;
    } else {
      sortField = field;
      sortOrder = true;
    }

    let sortFieldForEvent;

    switch (sortField) {
      case 'number':
        sortFieldForEvent = 'nomer';
        break;
      case 'area':
        sortFieldForEvent = 'ploshchad';
        break;
      case 'cost':
        sortFieldForEvent = 'cena';
        break;
      case 'price':
        sortFieldForEvent = 'za_m';
        break;
      case 'interior':
        sortFieldForEvent = 'otdelka';
        break;
      case 'apartments':
        sortFieldForEvent = 'komnat';
        break;
      case 'floor':
        sortFieldForEvent = 'etazh';
        break;
      default:
        return '';
    }

    this.handleTrackEvent({
      eventName: 'buildingChessListSortButtonsClick',
      sortField: sortFieldForEvent,
      eventContext: sortOrder ? 'po_vozrastaniyu' : 'po_ubyvaniyu',
    });

    const { handleSort } = this.props;
    handleSort(sortField, sortOrder);
  }

  handleTrackEvent = ({ eventName, cell, sortField, eventContext, pavilionName }) => {
    const { profile, objectType, building } = this.props;

    let eventContent;

    if (cell) {
      const { number, costFormatted, area, priceFormatted } = cell.data;
      eventContent = `${number} | ${costFormatted} | ${area} | ${priceFormatted}`;
    } else {
      eventContent = sortField;
    }

    trackGA(
      gaEvent[eventName]({
        eventContent: pavilionName || eventContent,
        eventContext,
        productType: objectType,
        productName: building.title,
        productID: building.id,
        user_id: profile.id,
      })
    );
  };

  renderHeaderItem(colKey) {
    const {
      sortColumn: [sortField, sortOrder],
      t,
    } = this.props;
    const col = getCols(t)[colKey];
    const style = col.name ? {} : { cursor: 'default' };

    return (
      <div
        style={style}
        className={cn(styles.HeaderItem, col.headerClassName)}
        onClick={col.name ? this.handleHeaderCellClick.bind(undefined, colKey) : null}
      >
        <div className={styles.HeaderText}>
          <span className={styles.TabletHidden}>
            {col.name && t(`components.building.list.${col.name}`)}
          </span>
          <span className={styles.TabletVisible}>
            {col.nameSm && t(`components.building.list.${col.nameSm}`)}
          </span>
        </div>
        {col.sort ? (
          <div className={styles.HeaderSort}>
            <AngleUp
              className={cn({ [styles.Sorted]: colKey === sortField && sortOrder === true })}
            />
            <AngleDown
              className={cn({ [styles.Sorted]: colKey === sortField && sortOrder === false })}
            />
          </div>
        ) : null}
      </div>
    );
  }

  renderHeader() {
    const { client, isSidebarHidden, typeId } = this.props;
    return (
      <div
        className={cn(
          styles.Header,
          { [styles.Client]: client },
          { [styles.Wide]: isSidebarHidden }
        )}
      >
        <div className={styles.HeaderContent}>
          {this.renderHeaderItem(typeId == 2 ? 'title' : 'number')}
          {this.renderHeaderItem('area')}
          {this.renderHeaderItem('cost')}
          {this.renderHeaderItem('price')}
          {this.renderHeaderItem('interior')}
          {this.renderHeaderItem('apartments')}
          {this.renderHeaderItem(typeId == 2 ? 'floorKp' : 'floor')}
          {this.renderHeaderItem('status')}
        </div>
      </div>
    );
  }

  renderCell(cell) {
    const { selected, typeId, t } = this.props;
    const { row, col } = cell;
    const isSelected = selected && selected[0] === row && selected[1] === col;

    const isPromo = false;
    const isReserve = true;

    return (
      <div key={`${row}:${col}`} className={cn(styles.Cell)}>
        <div
          onClick={this.handleCellClick.bind(undefined, row, col, cell)}
          className={cn(
            styles.CellContent,
            { [styles.Selected]: isSelected },
            { [styles.Promo]: isPromo },
            { [styles.Reserve]: isReserve }
          )}
        >
          <div className={cn(styles.CellItem, styles.CellNumber)}>
            <span>{printNumber(cell.data.number)}</span>
          </div>
          <div className={cn(styles.CellItem, styles.CellArea)}>
            <span>{cell.data.status !== 'fundStatus' && cell.data.area}</span>
          </div>
          <div className={cn(styles.CellItem, styles.CellCost)}>
            <span>
              {cell.data.status !== 'fundStatus' && +cell.data.costFormatted !== 0
                ? cell.data.costFormatted
                : ''}
            </span>
          </div>
          <div className={cn(styles.CellItem, styles.CellPrice)}>
            <span>
              {cell.data.status !== 'fundStatus' && +cell.data.priceFormatted !== 0
                ? cell.data.priceFormatted
                : ''}
            </span>
          </div>
          <div className={cn(styles.CellItem, styles.CellDecoration)}>
            <span>
              {cell.data.status !== 'fundStatus' &&
                cell.data.interiorName &&
                t(`components.building.chess.${cell.data.interiorName}`)}
            </span>
          </div>
          <div className={cn(styles.CellItem, styles.CellRoomCount)}>
            <span>
              {cell.data.status !== 'fundStatus' &&
                cell.data.apartmentsName &&
                t(`components.building.chess.${cell.data.apartmentsName}`)}
            </span>
          </div>
          <div className={cn(styles.CellItem, styles.CellFloorNumber)}>
            <span>{typeId == 2 ? cell.data.floors : cell.data.floorName}</span>
          </div>
          <div className={cn(styles.CellItem, styles.CellStatus)}>
            {cell.data.status === 'fundStatus' ? (
              <img src={iconLock} alt="" className={styles.iconFund} />
            ) : (
              <span>
                {cell.data.view_action === '1' ? (
                  <span className={styles.TagPromo}>
                    <i>{i18n.t('components.building.list.promo')}</i>
                  </span>
                ) : null}
                {isReserved(cell.data.status) ? (
                  <span className={styles.TagReserv}>
                    <i>{i18n.t('components.building.list.reserv')}</i>
                  </span>
                ) : null}
                {cell.data.status === 'assignmentStatus' ? (
                  <span className={styles.TagAssign}>
                    <i>{i18n.t('components.building.list.assign')}</i>
                  </span>
                ) : null}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderRows(cells = []) {
    return cells.map((cell) => this.renderCell(cell));
  }

  render() {
    const { className, client, isChessLoading, t } = this.props;
    const { cells = [], otherCells = [] } = this.state;
    const cellWithoutReserved = cells;
    const otherCellsWithoutReserved = otherCells;

    if (
      !isChessLoading &&
      cellWithoutReserved.length === 0 &&
      otherCellsWithoutReserved.length === 0
    ) {
      return (
        <div className={styles.FlatsSoldWrap}>
          <div className={styles.FlatsSold}>
            <p className={styles.FlatsSoldTitle}>{t('components.building.list.soldOutTitle')}</p>
            <img
              src={nothingFound}
              className={styles.FlatsSoldImage}
              alt={t('components.building.list.soldOutAlt')}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={cn(styles.Table, className, client ? styles.TableClient : '')}>
        {this.renderHeader()}
        <div className={styles.InnerBumper}></div>
        <div className={cn(styles.Inner, { [styles.Client]: client }, 'custom-scrollbar')}>
          <div className={cn(styles.Data, 'custom-scrollbar')}>
            {cellWithoutReserved.length && otherCells.length ? (
              <div className={styles.RowsTitle}>
                {t('components.building.list.cellWithoutReserved', {
                  cellWithoutReserved: cellWithoutReserved.length,
                })}
              </div>
            ) : null}
            <div className={styles.Rows}>{this.renderRows(cellWithoutReserved)}</div>
            {otherCellsWithoutReserved.length && otherCells.length ? (
              <div className={styles.RowsTitle}>
                {t('components.building.list.otherCells', {
                  otherCells: otherCells.length,
                })}
              </div>
            ) : null}
            <div className={styles.Rows}>{this.renderRows(otherCellsWithoutReserved)}</div>
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(List);



// WEBPACK FOOTER //
// ./src/components/Building/Chess/List/List.js